import { ConfigurableProperties as CP } from '@dora/backpack';
import * as RTK from '@reduxjs/toolkit';
import { NonEmptyArray } from 'fp-ts/lib/NonEmptyArray';
import { CurrentPropertySelections } from './selectors/configurableProperties';

/**
 * Properties in the current range that can be configured globally
 *
 * For each configurable property e.g. rattan_colour, we store both the "options" and
 * "current".
 *
 * "options": possible values the property can take (e.g. "Brown Weave", "Black Weave", etc.)
 * "current": the currently selected value in the scene (one of the options, e.g. "Brown Weave").
 *
 * Options and current can be set independently (options are derived from the products in the
 * current range, whereas current is either a default selected from the options or loaded from
 * a layout)
 *
 * Because of the nature of the Redux state we do not guarantee which order options or current will
 * be set in, and hence attempt to support them being set in either order. If current is set first,
 * it needs to be remembered until options are set. If options are set first, we need to set a default
 * current. If current is set afterwards, we override the default, if not we just go with it.
 *
 * This is achieved through the intermediary structure UnitialisedConfigurableProperty (used to
 * remember "current" if it's set first) and the function mergeCurrentIntoConfigurableProperty.
 */

/** A Configurable Property which does not have a selection */
export type ConfigurablePropertyOptions = {
    /** The key of a configurable property e.g. "rattan_colour" or "table_finish" */
    propertyKey: string;
    /** Customer facing label representing this property */
    label: string;
    /** Possible options this property can be set to */
    options: NonEmptyArray<CP.FinishT>;
};

/** State once properties have been initialised, includes the property and the current selection
 */
export type ConfigurableProperty = ConfigurablePropertyOptions & {
    /** Value this property is currently set to (must be present in options) */
    current: CP.FinishT;
};

/** Intermediate form of the state, used to remember current selections until the properties are
 * initialised
 */
export type CurrentConfigurableProperty = {
    propertyKey: string;
    current: CP.FinishT;
};

export type State = {
    properties: Array<ConfigurableProperty | CurrentConfigurableProperty> | null;
    propertiesFromLayout: CurrentPropertySelections | null;
};

const initialState: State = { properties: null, propertiesFromLayout: null };

/** Does the given selection exist in the given configurable property's options?
 */
const doesSelectionExist = (selection: CP.FinishT, cp: ConfigurableProperty) =>
    cp.options.some(o => CP.finishesEqual(o, selection));

export const isInitialised = (
    cp: ConfigurableProperty | CurrentConfigurableProperty,
): cp is ConfigurableProperty => cp && (cp as ConfigurableProperty).options !== undefined;

/** Point of the merge is to maintain the current value of configurable properties in a "smart" manner.
 *  If property is not initalised, set current to remember for later.
 *  If property has been initialised, set current only if it exists in options
 *  If current is undefined, make no changes
 *
 *  "current" refers to the value a configurable property is currently set to, please read top level
 *  comment.
 */
const mergeCurrentIntoConfigurableProperty = (
    cp: ConfigurableProperty | CurrentConfigurableProperty,
    current?: CP.FinishT,
): ConfigurableProperty | CurrentConfigurableProperty => {
    if (current && (!isInitialised(cp) || (isInitialised(cp) && doesSelectionExist(current, cp)))) {
        return { ...cp, current: current };
    }
    return cp;
};

export const slice = RTK.createSlice({
    name: 'catalogue',
    initialState,
    reducers: {
        /** Sets configurableProperties to an initialised set. Keeps any existing current property
         * selections for a property if they match one of the options for that property.
         *
         * options must be populated, if options is empty the property will be ignored.
         */
        setConfigurableProperties(state, action: RTK.PayloadAction<ConfigurablePropertyOptions[]>) {
            const configurablePropertiesWithDefaults = action.payload
                .map(payload => ({
                    ...payload,
                    // TODO: some more appropriate way of picking a default
                    current: payload.options[0],
                }));

            if (state.properties === null) {
                state.properties = configurablePropertiesWithDefaults;
            } else {
                state.properties = configurablePropertiesWithDefaults.map(cp => {
                    const currentFromState = state.properties?.find(
                        stateCp => stateCp.propertyKey === cp.propertyKey,
                    )?.current;

                    return mergeCurrentIntoConfigurableProperty(cp, currentFromState);
                });
            }
        },

        setPropertiesFromLayout(state, action: RTK.PayloadAction<CurrentPropertySelections>) {
            state.propertiesFromLayout = action.payload;
        },

        /** Set the current value of a given configurable property if it matches one of the
         * options in that property. If not, no changes are made.
         *
         * If the property has not yet been set, will remember the current value until it does
         * become set and perform the setting then using the same logic.
         */
        setConfigurablePropertySelection(
            state,
            action: RTK.PayloadAction<{ propertyKey: string; finish: CP.FinishT }>,
        ) {
            if (state.properties === null) {
                state.properties = [];
            }

            const propFromState = state.properties.find(
                cp => cp.propertyKey === action.payload.propertyKey,
            );
            if (!propFromState) {
                // property not yet initalised in the state, so setup an
                // UnitialisedConfigurableProperty to remember the payload option
                state.properties.push({
                    propertyKey: action.payload.propertyKey,
                    current: action.payload.finish,
                });
            } else {
                // property already exists in the state, replace the current with the payload option
                // if it matches one of the property options
                state.properties = state.properties.map(cp =>
                    cp.propertyKey === action.payload.propertyKey
                        ? mergeCurrentIntoConfigurableProperty(cp, action.payload.finish)
                        : cp,
                );
            }
        },
    },
});

export const {
    setConfigurableProperties,
    setConfigurablePropertySelection,
    setPropertiesFromLayout,
} = slice.actions;

export const reducer = slice.reducer;
